/**
 * Stable per-length identity key for stroke corrections.
 *
 * HealthKit exposes no per-lap UUID; the composite (offsetSeconds, durationSeconds,
 * duplicateOrdinal) is order-independent and stable across re-fetches (verified
 * across 112 firehoses / 7,472 lap events — zero collisions without the ordinal).
 * The ordinal is a belt-and-suspenders tiebreak for the rare synthetic duplicate.
 *
 * Pure module: no app-state imports, no side effects.
 */

export interface CorrectionKey {
  offsetSeconds: number;
  durationSeconds: number;
  duplicateOrdinal: number;
}

/**
 * Derive the composite key for a single lap.
 *
 * @param lapStartMs       Lap start time in milliseconds (from HealthKit event date)
 * @param workoutStartMs   Workout start time in milliseconds (from workout.startDate)
 * @param durationSeconds  Lap duration in (possibly fractional) seconds
 * @param duplicateOrdinal 0-based position within the (offsetSeconds, durationSeconds)
 *                         bucket; defaults to 0 — use assignOrdinals to stamp the real value
 */
export function deriveCorrectionKey(
  lapStartMs: number,
  workoutStartMs: number,
  durationSeconds: number,
  duplicateOrdinal = 0,
): CorrectionKey {
  return {
    offsetSeconds: Math.round((lapStartMs - workoutStartMs) / 1000),
    durationSeconds: Math.round(durationSeconds),
    duplicateOrdinal,
  };
}

/**
 * Assign duplicate ordinals across an array of laps and return one CorrectionKey
 * per lap (input order preserved).
 *
 * Ordinals are 0-based positions within each identical (offsetSeconds, durationSeconds)
 * bucket. Because the bucket is determined by the rounded key — not the array index —
 * the result is independent of input order: the same logical lap in a shuffled array
 * receives the same (offsetSeconds, durationSeconds, duplicateOrdinal) triple.
 *
 * PURE: returns new CorrectionKey[]; does not mutate the input.
 */
export function assignOrdinals<T extends { startMs: number; durationSeconds: number }>(
  laps: T[],
  workoutStartMs: number,
): CorrectionKey[] {
  // First pass: derive raw (offset, duration) for every lap, then count how many
  // times each bucket has been seen so far IN SORTED OFFSET ORDER — this makes
  // the ordinal assignment deterministic regardless of input order.
  const rawKeys = laps.map((lap) => ({
    offsetSeconds: Math.round((lap.startMs - workoutStartMs) / 1000),
    durationSeconds: Math.round(lap.durationSeconds),
  }));

  // Build a sorted version of the raw keys using UNROUNDED start/duration as the
  // tiebreak within each rounded bucket, so the physically-earlier lap always gets
  // ordinal 0 regardless of input order.
  const indexed = rawKeys.map((k, i) => ({
    ...k,
    originalIndex: i,
    rawStartMs: laps[i].startMs,
    rawDurationSeconds: laps[i].durationSeconds,
  }));
  const sorted = [...indexed].sort(
    (a, b) =>
      a.offsetSeconds - b.offsetSeconds ||
      a.durationSeconds - b.durationSeconds ||
      // Within a rounded bucket: sort by unrounded startMs, then unrounded duration
      a.rawStartMs - b.rawStartMs ||
      a.rawDurationSeconds - b.rawDurationSeconds ||
      // Stable final tiebreak by original position (truly-identical laps)
      a.originalIndex - b.originalIndex,
  );

  // Assign ordinals in that sorted order — order-independent by construction.
  const ordinals = new Array<number>(laps.length);
  const bucketCounts = new Map<string, number>();

  for (const { offsetSeconds, durationSeconds, originalIndex } of sorted) {
    const bucket = `${offsetSeconds}:${durationSeconds}`;
    const ordinal = bucketCounts.get(bucket) ?? 0;
    ordinals[originalIndex] = ordinal;
    bucketCounts.set(bucket, ordinal + 1);
  }

  return rawKeys.map((k, i) => ({
    offsetSeconds: k.offsetSeconds,
    durationSeconds: k.durationSeconds,
    duplicateOrdinal: ordinals[i],
  }));
}

/** Returns true iff all three identity fields are equal. */
export function keyEquals(a: CorrectionKey, b: CorrectionKey): boolean {
  return (
    a.offsetSeconds === b.offsetSeconds &&
    a.durationSeconds === b.durationSeconds &&
    a.duplicateOrdinal === b.duplicateOrdinal
  );
}

/**
 * Find which entry in `lapKeys` matches `key`.
 *
 * Matching strategy (in priority order):
 * 1. Exact (offsetSeconds, durationSeconds, duplicateOrdinal) — returns that index.
 * 2. Nearest offsetSeconds within ±2s that has the SAME durationSeconds AND
 *    duplicateOrdinal — returns the index with the smallest |delta|; on tie,
 *    the lowest index wins (deterministic).
 * 3. null — orphan; never misapply.
 */
export function matchKeyToLaps(key: CorrectionKey, lapKeys: CorrectionKey[]): number | null {
  // 1. Exact match
  for (let i = 0; i < lapKeys.length; i++) {
    if (keyEquals(key, lapKeys[i])) return i;
  }

  // 2. Jitter match: ±2s, same duration + ordinal.
  // MUST be unambiguous — if two candidates share the minimum delta, orphan (null).
  let bestIndex: number | null = null;
  let bestDelta = Infinity;
  let ambiguous = false;

  for (let i = 0; i < lapKeys.length; i++) {
    const lap = lapKeys[i];
    if (lap.durationSeconds !== key.durationSeconds) continue;
    if (lap.duplicateOrdinal !== key.duplicateOrdinal) continue;

    const delta = Math.abs(lap.offsetSeconds - key.offsetSeconds);
    if (delta > 2) continue;

    if (delta < bestDelta) {
      bestDelta = delta;
      bestIndex = i;
      ambiguous = false;
    } else if (delta === bestDelta) {
      ambiguous = true;
    }
  }

  return ambiguous ? null : bestIndex;
}
